import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  type DateType,
  DAY,
  formatTime,
  HOUR,
  MINUTE,
} from '@leland-dev/leland-ui-library';

import { TimeUnit } from '../__generated-gql-types__/globalTypes';

export const formatTimeRange = (startTime: DateType, endTime: DateType) => {
  return `${formatTime(startTime, { excludeTimeZone: true })} \u2013 ${formatTime(endTime)}`;
};

export const formatTimeUntil = (time: number) => {
  if (time <= 0) {
    return 'now!';
  }

  let remaining = time;

  const days = Math.floor(remaining / DAY);
  remaining -= days * DAY;

  const hours = Math.floor(remaining / HOUR);
  remaining -= hours * HOUR;

  const minutes = Math.floor(remaining / MINUTE);
  remaining -= minutes * MINUTE;

  const seconds = Math.floor(remaining / 1000);

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  } else if (days === 0 && hours === 0 && seconds > 0) {
    parts.push(`${seconds}s`);
  }

  if (parts.length === 0 && time > 0) {
    return `${seconds}s`;
  }

  return parts.join(' ');
};

export const timeUnitToTextMap: Record<TimeUnit, string> = {
  [TimeUnit.YEARS]: 'year',
  [TimeUnit.MONTHS]: 'month',
  [TimeUnit.WEEKS]: 'week',
  [TimeUnit.DAYS]: 'day',
};

export const timeUnitAdders: Record<
  TimeUnit,
  (date: DateType, amount: number) => DateType
> = {
  [TimeUnit.YEARS]: addYears,
  [TimeUnit.MONTHS]: addMonths,
  [TimeUnit.WEEKS]: addWeeks,
  [TimeUnit.DAYS]: addDays,
};

export enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export const isBusinessDay = (date: DateType) => {
  const dayOfWeek = new Date(date).getDay() as DayOfWeek;
  return dayOfWeek !== DayOfWeek.SATURDAY && dayOfWeek !== DayOfWeek.SUNDAY;
};

export const getNextBusinessDay = (date: DateType): DateType => {
  const nextDay = addDays(date, 1);
  return isBusinessDay(nextDay) ? nextDay : getNextBusinessDay(nextDay);
};
